import { Injectable, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Centralized icon registration service
 * 
 * This service registers all SVG icons once at application startup,
 * ensuring they are loaded only once and cached properly.
 * 
 * Benefits:
 * - Icons are registered once globally, not per component instance
 * - Reduces HTTP requests for SVG assets
 * - Works with the asset caching interceptor for optimal performance
 */
@Injectable({
  providedIn: 'root'
})
export class IconRegistryService {
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private registeredIcons = new Set<string>();

  /**
   * Icon definitions organized by category
   */
  private readonly iconDefinitions = {
    // Chunk/Step control icons
    controls: [
      'control-code',
      'control-output',
      'control-edit',
      'state-spinner'
    ],
    // Editor toolbar icons
    editor: [
      'bold',
      'italic',
      'underline',
      'code',
      'quotes',
      'ol',
      'ul',
      'picture',
      'chains',
      'undo',
      'redo'
    ],
    // Chunk action icons
    chunks: [
      'chunk-code',
      'chunk-collapse',
      'chunk-edit',
      'chunk-enable-disable',
      'chunk-outputs',
      'chunk-run',
      'chunk-run-arrow'
    ],
    // Role icons (registered with both 'role-' prefix and without for compatibility)
    roles: [
      'owner',
      'owner-light',
      'admin',
      'admin-light',
      'contributor',
      'contributor-light',
      'operator',
      'operator-light'
    ],
    // UI icons
    ui: [
      'add-button',
      'arrow-down',
      'arrow-up',
      'clock',
      'copy',
      'cross',
      'curly-brackets',
      'dash',
      'import',
      'search',
      'search-icon'
    ],
    // Connection type icons
    connections: [
      'connection-database',
      'connection-datafusion',
      'connection-object-storage',
      'connection-box',
      'connection-cloud',
      'connection-settings',
      'connection-s3',
      'connection-gcs',
      'connection-aws',
      'connection-azure'
    ]
  };

  /**
   * Register all icons at application startup
   * This method should be called once during app initialization
   */
  registerAllIcons(): void {
    console.log('🎨 Registering SVG icons...');
    
    const startTime = performance.now();
    let registeredCount = 0;

    // Register all icons from all categories
    Object.entries(this.iconDefinitions).forEach(([category, icons]) => {
      icons.forEach(iconName => {
        this.registerIcon(iconName);
        registeredCount++;
        
        // For role icons, also register with 'role-' prefix for backward compatibility
        if (category === 'roles' && !iconName.includes('-light')) {
          this.registerIcon(`role-${iconName}`, `assets/icons/${iconName}.svg`);
          registeredCount++;
        }
      });
    });

    const duration = Math.round(performance.now() - startTime);
    console.log(`✅ Registered ${registeredCount} SVG icons in ${duration}ms`);
  }

  /**
   * Register a single icon
   * @param iconName - Name of the icon (without .svg extension)
   * @param iconPath - Optional custom path (defaults to assets/icons/{iconName}.svg)
   */
  registerIcon(iconName: string, iconPath?: string): void {
    // Skip if already registered
    if (this.registeredIcons.has(iconName)) {
      return;
    }

    const path = iconPath || `assets/icons/${iconName}.svg`;
    const url = this.domSanitizer.bypassSecurityTrustResourceUrl(path);
    
    this.matIconRegistry.addSvgIcon(iconName, url);
    this.registeredIcons.add(iconName);
  }

  /**
   * Register multiple icons at once
   * @param iconNames - Array of icon names to register
   */
  registerIcons(iconNames: string[]): void {
    iconNames.forEach(iconName => this.registerIcon(iconName));
  }

  /**
   * Check if an icon is already registered
   * @param iconName - Name of the icon to check
   */
  isIconRegistered(iconName: string): boolean {
    return this.registeredIcons.has(iconName);
  }

  /**
   * Get all registered icon names
   */
  getRegisteredIcons(): string[] {
    return Array.from(this.registeredIcons);
  }
}
